<template>
  <div class="admin-mail admin-dashboard">
    <v-container fluid class="py-4">
      <!-- Header -->
      <div class="d-flex align-center justify-space-between mb-4 flex-wrap" style="gap: 12px">
        <div class="d-flex align-center" style="gap: 12px">
          <v-icon size="32" color="primary">mdi-email-multiple-outline</v-icon>
          <div>
            <h1 class="text-h5 font-weight-bold mb-0">Webmail</h1>
            <div class="text-caption text-medium-emphasis">{{ currentAddress || 'жүктелуде...' }}</div>
          </div>
        </div>
        <div class="d-flex" style="gap: 8px">
          <v-btn
            color="primary"
            variant="flat"
            prepend-icon="mdi-pencil-plus"
            @click="composeOpen = true"
          >Жаңа хат</v-btn>
          <v-btn
            variant="outlined"
            icon="mdi-refresh"
            :loading="loading"
            @click="loadInbox"
          />
          <v-btn
            variant="outlined"
            icon="mdi-cog-outline"
            @click="passwordOpen = true"
          />
        </div>
      </div>

      <!-- Stats -->
      <v-row class="mb-4">
        <v-col cols="6" sm="3">
          <v-card variant="tonal" color="primary" class="pa-3 text-center">
            <div class="text-caption">Барлығы</div>
            <div class="text-h5 font-weight-bold">{{ total }}</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3">
          <v-card variant="tonal" color="warning" class="pa-3 text-center">
            <div class="text-caption">Оқылмаған</div>
            <div class="text-h5 font-weight-bold">{{ unseen }}</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Health alert -->
      <v-alert
        v-if="healthError"
        type="error"
        variant="tonal"
        class="mb-4"
        closable
        @click:close="healthError = null"
      >
        Пошта серверіне қосылу мүмкін болмады: {{ healthError }}
      </v-alert>

      <!-- Inbox list -->
      <v-card>
        <v-list lines="two" density="comfortable">
          <template v-if="loading">
            <v-list-item v-for="i in 5" :key="i">
              <v-skeleton-loader type="list-item-two-line" />
            </v-list-item>
          </template>
          <template v-else-if="!messages.length">
            <v-list-item>
              <v-list-item-title class="text-center text-medium-emphasis py-6">
                Inbox бос
              </v-list-item-title>
            </v-list-item>
          </template>
          <template v-else>
            <v-list-item
              v-for="msg in messages"
              :key="msg.uid"
              :class="{ 'msg-unseen': !msg.seen }"
              @click="openMessage(msg)"
            >
              <template #prepend>
                <v-icon :color="msg.seen ? 'grey' : 'primary'">
                  {{ msg.seen ? 'mdi-email-open-outline' : 'mdi-email' }}
                </v-icon>
              </template>
              <v-list-item-title class="d-flex align-center" style="gap: 8px">
                <span :class="{ 'font-weight-bold': !msg.seen }">
                  {{ msg.from?.name || msg.from?.address || '—' }}
                </span>
                <span class="text-caption text-medium-emphasis ms-auto">
                  {{ formatDate(msg.date) }}
                </span>
              </v-list-item-title>
              <v-list-item-subtitle :class="{ 'font-weight-medium': !msg.seen }">
                {{ msg.subject || '(тақырыпсыз)' }}
              </v-list-item-subtitle>
              <template #append>
                <v-btn
                  icon="mdi-delete-outline"
                  size="small"
                  variant="text"
                  @click.stop="deleteMessage(msg)"
                />
              </template>
            </v-list-item>
          </template>
        </v-list>
      </v-card>
    </v-container>

    <!-- Read dialog -->
    <v-dialog v-model="readOpen" max-width="800" scrollable>
      <v-card v-if="current">
        <v-card-title class="d-flex align-center" style="gap: 8px">
          <v-icon>mdi-email-outline</v-icon>
          <span class="text-truncate">{{ current.subject || '(тақырыпсыз)' }}</span>
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" @click="readOpen = false" />
        </v-card-title>
        <v-divider />
        <v-card-text class="py-4">
          <div class="mb-3">
            <div class="text-caption text-medium-emphasis">Кімнен:</div>
            <div class="text-body-2">
              {{ current.from?.name }} &lt;{{ current.from?.address }}&gt;
            </div>
          </div>
          <div class="mb-3">
            <div class="text-caption text-medium-emphasis">Уақыты:</div>
            <div class="text-body-2">{{ formatFullDate(current.date) }}</div>
          </div>
          <v-divider class="my-4" />
          <div
            v-if="current.html"
            v-html="current.html"
            class="mail-html-content"
          />
          <pre v-else class="mail-text-content">{{ current.text }}</pre>

          <div v-if="current.attachments?.length" class="mt-4">
            <div class="text-caption text-medium-emphasis mb-2">Файлдар:</div>
            <v-chip
              v-for="a in current.attachments"
              :key="a.filename"
              size="small"
              variant="tonal"
              class="me-2 mb-1"
            >
              <v-icon start size="14">mdi-paperclip</v-icon>
              {{ a.filename }} ({{ formatSize(a.size) }})
            </v-chip>
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-btn
            color="primary"
            variant="text"
            prepend-icon="mdi-reply"
            @click="replyTo(current)"
          >Жауап беру</v-btn>
          <v-spacer />
          <v-btn
            color="error"
            variant="text"
            prepend-icon="mdi-delete-outline"
            @click="deleteMessage(current); readOpen = false"
          >Жою</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Compose dialog -->
    <v-dialog v-model="composeOpen" max-width="700" persistent>
      <v-card>
        <v-card-title class="d-flex align-center" style="gap: 8px">
          <v-icon>mdi-email-edit-outline</v-icon>
          Жаңа хат
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" @click="composeOpen = false" />
        </v-card-title>
        <v-divider />
        <v-card-text class="py-4">
          <v-text-field
            v-model="compose.to"
            label="Кімге"
            placeholder="example@gmail.com"
            variant="outlined"
            density="compact"
            class="mb-3"
            :rules="[v => /.+@.+\..+/.test(v) || 'Дұрыс email енгізіңіз']"
          />
          <v-text-field
            v-model="compose.subject"
            label="Тақырыбы"
            variant="outlined"
            density="compact"
            class="mb-3"
          />
          <v-textarea
            v-model="compose.text"
            label="Хат мәтіні"
            variant="outlined"
            rows="10"
            auto-grow
          />
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-btn variant="text" @click="composeOpen = false">Бас тарту</v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            variant="flat"
            prepend-icon="mdi-send"
            :loading="sending"
            :disabled="!canSend"
            @click="sendMail"
          >Жіберу</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Settings dialog (address + password) -->
    <v-dialog v-model="passwordOpen" max-width="560" persistent>
      <v-card>
        <v-card-title class="d-flex align-center" style="gap: 8px">
          <v-icon>mdi-cog-outline</v-icon>
          Пошта баптаулары
        </v-card-title>
        <v-divider />
        <v-card-text class="py-4">
          <v-alert type="info" variant="tonal" density="compact" class="mb-4">
            Өзгерістен кейін IMAP/SMTP клиенттерінде (Thunderbird, телефон) да жаңартыңыз.
          </v-alert>

          <!-- Address -->
          <div class="text-subtitle-2 mb-2">📧 Email мекенжайы</div>
          <v-text-field
            v-model="newAddress"
            label="info@korkemauto.kz"
            variant="outlined"
            density="compact"
            class="mb-1"
            :rules="[v => !v || /.+@.+\..+/.test(v) || 'Дұрыс email енгізіңіз']"
          />
          <v-btn
            color="primary"
            variant="outlined"
            size="small"
            prepend-icon="mdi-account-edit-outline"
            :loading="changingAddress"
            :disabled="!newAddress || newAddress === currentAddress"
            class="mb-4"
            @click="changeAddress"
          >Мекенжайды сақтау</v-btn>

          <v-divider class="mb-4" />

          <!-- Password -->
          <div class="text-subtitle-2 mb-2">🔑 Пароль</div>
          <v-text-field
            v-model="newPassword"
            label="Жаңа пароль (мин. 8 таңба)"
            :type="showPass ? 'text' : 'password'"
            variant="outlined"
            density="compact"
            :append-inner-icon="showPass ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append-inner="showPass = !showPass"
            :rules="[v => !v || v.length >= 8 || 'Кемінде 8 таңба']"
          />
          <v-btn
            color="primary"
            variant="outlined"
            size="small"
            prepend-icon="mdi-key-change"
            :loading="changingPassword"
            :disabled="!newPassword || newPassword.length < 8"
            @click="changePassword"
          >Парольді сақтау</v-btn>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="passwordOpen = false">Жабу</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import api from '@/utils/api'
import { useSnackbarStore } from '@/stores/snackbar'

const snackbar = useSnackbarStore()

const loading = ref(false)
const sending = ref(false)
const changingPassword = ref(false)
const total = ref(0)
const unseen = ref(0)
const messages = ref([])
const healthError = ref(null)

const readOpen = ref(false)
const composeOpen = ref(false)
const passwordOpen = ref(false)
const current = ref(null)

const compose = ref({ to: '', subject: '', text: '' })
const newPassword = ref('')
const showPass = ref(false)
const newAddress = ref('')
const currentAddress = ref('')
const changingAddress = ref(false)

const canSend = computed(() =>
  /.+@.+\..+/.test(compose.value.to) &&
  compose.value.subject.trim().length > 0 &&
  compose.value.text.trim().length > 0
)

const loadInbox = async () => {
  loading.value = true
  healthError.value = null
  try {
    const { data } = await api.get('/admin/mail/inbox', { params: { limit: 50 } })
    total.value = data.total
    unseen.value = data.unseen
    messages.value = data.messages
  } catch (err) {
    healthError.value = err.response?.data?.error || err.message
  } finally {
    loading.value = false
  }
}

const openMessage = async (msg) => {
  try {
    const { data } = await api.get(`/admin/mail/message/${msg.uid}`)
    current.value = data
    readOpen.value = true
    msg.seen = true
    if (unseen.value > 0) unseen.value -= 1
  } catch (err) {
    snackbar.error(err.response?.data?.error || err.message)
  }
}

const deleteMessage = async (msg) => {
  if (!confirm('Хатты жоюды растайсыз ба?')) return
  try {
    await api.delete(`/admin/mail/message/${msg.uid}`)
    messages.value = messages.value.filter(m => m.uid !== msg.uid)
    total.value = Math.max(0, total.value - 1)
    snackbar.success('Хат жойылды')
  } catch (err) {
    snackbar.error(err.response?.data?.error || err.message)
  }
}

const replyTo = (msg) => {
  const addr = msg.from?.address || ''
  compose.value = {
    to: addr,
    subject: msg.subject?.startsWith('Re:') ? msg.subject : `Re: ${msg.subject || ''}`,
    text: `\n\n---\n${msg.from?.name || addr} жазды:\n${msg.text || ''}`
  }
  readOpen.value = false
  composeOpen.value = true
}

const sendMail = async () => {
  sending.value = true
  try {
    await api.post('/admin/mail/send', {
      to: compose.value.to,
      subject: compose.value.subject,
      text: compose.value.text
    })
    snackbar.success('Хат жіберілді')
    composeOpen.value = false
    compose.value = { to: '', subject: '', text: '' }
  } catch (err) {
    snackbar.error(err.response?.data?.error || err.message)
  } finally {
    sending.value = false
  }
}

const loadAccount = async () => {
  try {
    const { data } = await api.get('/admin/mail/account')
    currentAddress.value = data.address
    newAddress.value = data.address
  } catch {}
}

const changeAddress = async () => {
  changingAddress.value = true
  try {
    const { data } = await api.put('/admin/mail/account', { address: newAddress.value })
    currentAddress.value = data.address
    snackbar.success(data.notice || 'Мекенжай өзгертілді')
  } catch (err) {
    snackbar.error(err.response?.data?.error || err.message)
  } finally {
    changingAddress.value = false
  }
}

const changePassword = async () => {
  changingPassword.value = true
  try {
    await api.post('/admin/mail/password', { newPassword: newPassword.value })
    snackbar.success('Пароль өзгертілді')
    passwordOpen.value = false
    newPassword.value = ''
  } catch (err) {
    snackbar.error(err.response?.data?.error || err.message)
  } finally {
    changingPassword.value = false
  }
}

const formatDate = (d) => {
  if (!d) return ''
  const date = new Date(d)
  const now = new Date()
  const sameDay = date.toDateString() === now.toDateString()
  if (sameDay) {
    return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
  }
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })
}

const formatFullDate = (d) => {
  if (!d) return ''
  return new Date(d).toLocaleString('ru-RU')
}

const formatSize = (bytes) => {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

onMounted(() => {
  loadInbox()
  loadAccount()
})
</script>

<style scoped>
.admin-mail {
  min-height: 100vh;
}
.msg-unseen {
  background: rgba(var(--v-theme-primary), 0.04);
}
.mail-html-content {
  max-width: 100%;
  overflow-x: auto;
}
.mail-html-content :deep(img) {
  max-width: 100%;
  height: auto;
}
.mail-text-content {
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: inherit;
  margin: 0;
}
</style>
